import { CalendarClock } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Separator,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@design-system';
import { useCarePlanSummary } from '../../hooks/useCarePlanSummary';
import { ClaimantBadge } from '../projectionTable/ClaimantBadge';
import type { CarePlanSummary } from './buildCarePlanSummary';

/**
 * Read-only popover mirroring the Edit Care Assumptions screen
 * (Care Start Age / Care Timeline / Inflation Rate). Display only — no
 * sliders, selects, or steppers. Values come from `useCarePlanSummary`.
 *
 * For joint planning the partner's plan is appended below the client's,
 * separated by a divider; each section is tagged with the same client/partner
 * badge used by the monthly breakdown table.
 */
export function CarePlanDetailsPopover() {
  const { client, partner } = useCarePlanSummary();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <CalendarClock className="h-4 w-4" />
          Care plan details
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[min(70vh,var(--radix-popover-content-available-height))] w-[560px] max-w-[90vw] overflow-auto"
      >
        <div className="space-y-6">
          <CarePlanSection summary={client} claimant={partner ? 'client' : null} />
          {partner && (
            <>
              <Separator />
              <CarePlanSection summary={partner} claimant="partner" />
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * One person's care plan (Care Start Age / Inflation Rate / Care Timeline).
 * `claimant` drives the badge; pass `null` for single-life clients to omit it.
 */
function CarePlanSection({
  summary,
  claimant,
}: {
  summary: CarePlanSummary;
  claimant: 'client' | 'partner' | null;
}) {
  const { showCareStartAge, careStartAge, inflationRatePct, phases } = summary;

  return (
    <div className="space-y-6">
      {claimant && <ClaimantBadge claimant={claimant} />}
      <div className="flex flex-wrap gap-x-10 gap-y-4">
        {showCareStartAge && (
          <section className="space-y-1">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
              Care Start Age
            </h3>
            <div className="flex items-baseline gap-2">
              <span className="text-primary-dark text-3xl font-bold">{careStartAge}</span>
              <span className="text-base text-gray-500">years old</span>
            </div>
          </section>
        )}

        <section className="space-y-1">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
            Inflation Rate
          </h3>
          <div className="flex items-baseline gap-2">
            <span className="text-primary-dark text-3xl font-bold">{inflationRatePct}%</span>
          </div>
        </section>
      </div>

      <section className="space-y-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
          Care Timeline
        </h3>
        <Table>
          <TableHeader>
            <TableRow className="border-b border-gray-200 hover:bg-transparent">
              <TableHead className="h-auto px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Phase
              </TableHead>
              <TableHead className="h-auto px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Est. Begin
              </TableHead>
              <TableHead className="h-auto px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Environment
              </TableHead>
              <TableHead className="h-auto px-2 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Duration (mo)
              </TableHead>
              <TableHead className="h-auto px-2 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Unit Cost
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {phases.map(phase => (
              <TableRow key={phase.label} className="border-b border-gray-100 last:border-0">
                <TableCell className="px-2 py-1.5">
                  <span className="text-xs font-semibold text-gray-900">{phase.label}</span>
                </TableCell>
                <TableCell className="px-2 py-1.5">
                  {phase.ageYears != null ? (
                    <span className="text-primary-dark text-xs font-medium">
                      {phase.ageYears} yrs
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </TableCell>
                <TableCell className="px-2 py-1.5">
                  <span className="text-xs text-gray-700">
                    {phase.environmentLabel ?? '-'}
                    {phase.isPredicted && <span className="text-gray-400"> (Predicted)</span>}
                  </span>
                </TableCell>
                <TableCell className="px-2 py-1.5 text-center">
                  <span className="text-xs font-medium text-gray-900">{phase.durationMonths}</span>
                </TableCell>
                <TableCell className="px-2 py-1.5 text-right">
                  <span className="text-xs font-medium text-gray-900">{phase.unitCostDisplay}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}
